import { PlayIcon, StackIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { memo } from "react";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { getDateFromMilliseconds } from "../../helpers/DateTime";
import { AreaButton, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";
import { ActivityMetaRow } from "./ActivityMetaRow";
import { GREEN_OUTLINE_PILL_BUTTON_CLASS } from "./classMemberActivityButtonStyles";
import { ClassMemberActivityCardShell } from "./ClassMemberActivityCardShell";

interface VocabularyTrainerCardProps {
    trainingId: string;
    name: string;
    /** Omitted → the word-count segment of the meta row is hidden. */
    wordCount?: number;
    /** Epoch ms; omitted → the date segment of the meta row is hidden. */
    assignedDate?: number;
    /** Empty/undefined → the action button is not rendered (no dead links). */
    shareLink?: string;
    /** The share link must not be opened (e.g. classroom preview): the action renders as a plain button. */
    isBlocked?: boolean;
    /** Click handler for the action; typically explains the blocked state when isBlocked is set. */
    onLearnClick?: () => void;
}

const VocabularyTrainerCard = (props: VocabularyTrainerCardProps) => {
    const t = useT();

    const t_vocabCategory = t("Vocabulary trainer", { _tags: "ClassMemberDashboard, VocabularyTrainerCard" });
    const t_learn = t("Learn now", { _tags: "ClassMemberDashboard, VocabularyTrainerCard" });
    const t_word = t("{count} word", {
        _tags: "ClassMemberDashboard, VocabularyTrainerCard",
        count: props.wordCount,
    });
    const t_words = t("{count} words", {
        _tags: "ClassMemberDashboard, VocabularyTrainerCard",
        count: props.wordCount,
    });
    const t_assignedOn = t("Assigned on {date}", {
        _tags: "ClassMemberDashboard, VocabularyTrainerCard",
        date: props.assignedDate ? getDateFromMilliseconds(props.assignedDate) : "",
    });

    const renderBadgeIcon = (iconSize: string) => (
        <PhosphorIcon
            phosphorIcon={StackIcon}
            fontSize={iconSize}
            color="text-forest-green-800"
        />
    );

    const renderMetaRow = () => {
        const parts: string[] = [];
        if (props.wordCount !== undefined) parts.push(props.wordCount === 1 ? t_word : t_words);
        if (props.assignedDate) parts.push(t_assignedOn);
        if (parts.length === 0) return null;
        return <ActivityMetaRow parts={parts} />;
    };

    const renderAction = () => {
        if (!props.shareLink) return null;
        return (
            <AreaButton
                variant={ButtonVariant.SECONDARY}
                role={props.isBlocked ? "button" : "link"}
                href={props.isBlocked ? undefined : props.shareLink}
                openInNewTab={!props.isBlocked}
                onClick={props.onLearnClick}
                phosphorIcon={PlayIcon}
                iconPosition={IconPosition.RIGHT}
                className={GREEN_OUTLINE_PILL_BUTTON_CLASS}
            >
                {t_learn}
            </AreaButton>
        );
    };

    return (
        <ClassMemberActivityCardShell
            category={ClassActivityCategory.VOCAB_TRAINING}
            renderBadgeIcon={renderBadgeIcon}
            categoryLabel={t_vocabCategory}
            name={props.name}
            subInfo={renderMetaRow()}
            action={renderAction()}
            data-testid={`classroom-vocab-${props.trainingId}`}
        />
    );
};

export default memo(VocabularyTrainerCard);
